import { gql, useMutation } from '@apollo/client'
import { Modal2 } from 'components/Modal'
import SubmitButton from 'components/SubmitButton'
import TextInput from 'components/TextInput'
import { Field, Form, Formik } from 'formik'
import { range } from 'lodash'
import { useToast } from './Toast'
import { ReshipOrderMutation, ReshipOrderMutationVariables } from './__generated__/ReshipOrderMutation'

const reshipOrderMutation = gql`
  mutation ReshipOrderMutation($input: ReshipOrderInput!) {
    reshipOrder(input: $input) {
      success
      message
      order {
        id
        status
      }
    }
  }
`

interface ShipmentLine {
  product: string
  quantity: number
}

interface ReshipOrderModalProps {
  orderId: string
  onRequestClose: () => void
  initialShipmentLines: ShipmentLine[]
}

const ReshipOrderModal = ({ orderId, onRequestClose, initialShipmentLines }: ReshipOrderModalProps) => {
  const [reshipOrder] = useMutation<ReshipOrderMutation, ReshipOrderMutationVariables>(reshipOrderMutation, {
    refetchQueries: ['OrderByIdQuery'],
  })

  const { addToast } = useToast()

  return (
    <Modal2 isOpen onRequestClose={onRequestClose} header="Re-Ship Order">
      <Formik
        initialValues={{
          zdRef: '',
          reason: '',
          shipmentLines: initialShipmentLines,
        }}
        onSubmit={async (values, { setStatus }) => {
          try {
            const resp = await reshipOrder({
              variables: {
                input: {
                  orderId,
                  reason: values.reason,
                  zdRef: values.zdRef,
                  shipmentLines: values.shipmentLines.map((line) => ({
                    product: line.product,
                    quantity: Number(line.quantity),
                  })),
                },
              },
            })

            if (!resp.data.reshipOrder.success) {
              throw new Error(resp.data.reshipOrder.message || 'Something went wrong')
            }

            onRequestClose()
            addToast({
              messageType: 'success',
              title: 'Re-shipped',
              content: <>New shipment created</>,
              timeout: 5000,
            })
          } catch (err) {
            setStatus({ error: err.message })
          }
        }}
      >
        {({ isSubmitting, status, values }) => (
          <Form>
            <table className="table is-bordered is-fullwidth">
              <thead>
                <tr>
                  <th>DPD Product Code</th>
                  <th>Original qty</th>
                  <th>New Qty</th>
                </tr>
              </thead>
              <tbody>
                {values.shipmentLines.map((line, i) => (
                  <tr key={i}>
                    <td>{line.product}</td>
                    <td>{initialShipmentLines[i].quantity}</td>
                    <td>
                      <div className="ml-2 select is-small">
                        <Field component="select" name={`shipmentLines[${i}].quantity`}>
                          {range(0, initialShipmentLines[i].quantity + 1).map((qty) => (
                            <option key={qty} value={qty}>
                              {qty}
                            </option>
                          ))}
                        </Field>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TextInput name="reason" label="Reason for re-ship" />
            <TextInput name="zdRef" label="Zendesk ticket no (if applicable)" />

            <div className="buttons is-right mt-5">
              <SubmitButton className="is-primary" submitting={isSubmitting} disabled={isSubmitting}>
                Re-Ship Order
              </SubmitButton>
            </div>
            {status?.error && (
              <article className="message is-danger">
                <div className="message-body">Failed: {status.error}</div>
              </article>
            )}
          </Form>
        )}
      </Formik>
    </Modal2>
  )
}

export default ReshipOrderModal
